import { useState, useEffect } from "react";

export const useVariantStock = (product, selectedVariation) => {
  const [stock, setStock] = useState(null); // null = loading, number = stock
  const [price, setPrice] = useState(null); // null = loading, number = price
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // This tracks which variation index the current state belongs to
  const [fetchedVariationIndex, setFetchedVariationIndex] = useState(null);

  // Logic: If the selectedVariation doesn't match our fetched index, the data is "stale"
  const isStale = fetchedVariationIndex !== selectedVariation;

  useEffect(() => {
    // 1. Validation: Ensure we have the data needed to make a request
    if (
      !product ||
      selectedVariation === undefined ||
      !product.variations ||
      !product.variations[selectedVariation]?.stripeId
    ) {
      setStock(null);
      setPrice(null);
      setError("Missing product or variation data");
      return;
    }

    const fetchStockAndPrice = async () => {
      setLoading(true);
      setError(null);

      // Capture the index at the start of the async call to prevent race conditions
      const currentIdx = selectedVariation;

      try {
        const shopifyDomain = process.env.REACT_APP_SHOPIFY_DOMAIN;
        const storefrontAccessToken =
          process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

        if (!shopifyDomain || !storefrontAccessToken) {
          throw new Error("Shopify environment variables missing");
        }

        const stripeId = product.variations[currentIdx].stripeId;
        const variantId = `gid://shopify/ProductVariant/${stripeId}`;

        const graphqlEndpoint = `https://${shopifyDomain}/api/2025-07/graphql.json`;

        const query = `
          query getVariantStockAndPrice($id: ID!) {
            node(id: $id) {
              id
              __typename
              ... on ProductVariant {
                quantityAvailable
                availableForSale
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        `;

        const variables = { id: variantId };

        const response = await fetch(graphqlEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseBody = await response.json();
        const { data, errors } = responseBody;

        if (errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(errors)}`);
        }

        if (!data?.node || data.node.__typename !== "ProductVariant") {
          throw new Error(
            `Variant not found or incorrect node type for ID: ${variantId}`
          );
        }

        const { quantityAvailable, price: priceData } = data.node;

        // 2. Guard: Only update state if the user hasn't switched variations while we were waiting
        if (currentIdx === selectedVariation) {
          setStock(quantityAvailable ?? 0);
          setPrice(priceData?.amount ? parseFloat(priceData.amount) : null);
          setFetchedVariationIndex(currentIdx); // Successfully synced state to this index
        }
      } catch (err) {
        console.error("Stock and price fetch error:", err);

        // Only set error state if this request is still the relevant one
        if (currentIdx === selectedVariation) {
          setError(err.message);
          setStock(0);
          setPrice(null);
          setFetchedVariationIndex(currentIdx);
        }
      } finally {
        if (currentIdx === selectedVariation) {
          setLoading(false);
        }
      }
    };

    fetchStockAndPrice();
  }, [product, selectedVariation]);

  // 3. Return Logic:
  // If the hook is currently fetching, or if the stored data belongs to a different variation,
  // we return null/loading. This triggers your UI fallbacks (like the ?? 1)
  // instead of showing a "0" from a previous variation.
  return {
    stock: isStale ? null : stock,
    price: isStale ? null : price,
    loading: loading || isStale,
    error,
  };
};

export default useVariantStock;
